import React, { useEffect, useRef, useState } from "react";
import { TollServices } from "../../../services/TollServices";

interface TollPriceDisplayProps {
    "fromLat": number;
    "fromLng": number;
    "toLat":   number;
    "toLng":   number;

    /** Wear / operating cost per km in CHF (from pricing slice) */
    "wearCostPerKmChf":     number;
    "className"?:           string;
    "showIcon"?:            boolean;
    "compact"?:             boolean;
    "onPriceCalculated"?:   ( price: number | null ) => void;
    "onDetailsCalculated"?: ( details: {
        "tollPrice":  number;
        "distance":   number;
        "totalPrice": number;
    } | null ) => void;
}

const TollPriceDisplay: React.FC<TollPriceDisplayProps> = ( {
    fromLat,
    fromLng,
    toLat,
    toLng,
    wearCostPerKmChf,
    className = "",

    compact = false,
    onPriceCalculated,
    onDetailsCalculated,
} ) => {
    const [ tollPrice, setTollPrice ] = useState<number | null>( null );
    const [ isLoading, setIsLoading ] = useState<boolean>( true );
    const [ error, setError ] = useState<string | null>( null );
    const [ baseRoute, setBaseRoute ] = useState<{
        "tollPrice": number;
        "distance":  number;
    } | null>( null );

    const onDetailsRef = useRef( onDetailsCalculated );
    const onPriceRef = useRef( onPriceCalculated );

    useEffect( () => {
        onDetailsRef.current = onDetailsCalculated;
        onPriceRef.current = onPriceCalculated;
    } );

    /*
     * Fetch toll + distance only when endpoints change.
     * Wear cost is applied locally so editing wear in the modal does not hit the toll API again.
     */
    useEffect( () => {
        const fetchTollRoute = async() => {
            setIsLoading( true );
            setError( null );
            setBaseRoute( null );

            try {
                const tollService = new TollServices();
                const routeDetails = await tollService.calculateTollRoute( fromLat, fromLng, toLat, toLng );

                const costValue = typeof routeDetails.cost === "string" ? routeDetails.cost : String( routeDetails.cost ?? "0" );
                const tollPriceCalculated = parseFloat( costValue );
                const distanceValue: number = typeof routeDetails.distance === "number" ? routeDetails.distance : 0;

                setBaseRoute( {
                    "tollPrice": tollPriceCalculated,
                    "distance":  distanceValue,
                } );
            } catch( err ) {
                console.error( "Failed to fetch toll price:", err );
                setError( "Unable to calculate toll price" );
                setTollPrice( null );
                onPriceRef.current?.( null );
                onDetailsRef.current?.( null );
            } finally {
                setIsLoading( false );
            }
        };

        if ( ( fromLat !== 0 ) && ( fromLng !== 0 ) && ( toLat !== 0 ) && ( toLng !== 0 ) ) {
            void fetchTollRoute();
        }
    }, [ fromLat, fromLng, toLat, toLng ] );

    useEffect( () => {
        if ( baseRoute === null ) {
            return;
        }

        const wearCost = baseRoute.distance * wearCostPerKmChf;
        const totalPrice = baseRoute.tollPrice + wearCost;

        onDetailsRef.current?.( {
            "tollPrice": baseRoute.tollPrice,
            "distance":  baseRoute.distance,
            totalPrice,
        } );
        setTollPrice( totalPrice );
        onPriceRef.current?.( totalPrice );
    }, [ baseRoute, wearCostPerKmChf ] );

    // Si onPriceCalculated est fourni, on ne rend rien (composant invisible)
    if ( onPriceCalculated !== null ) {
        return null;
    }

    if ( isLoading ) {
        return (
            <div className={`toll-price-card rounded shadow p-3 ${ className }`}>
                <div className="flex items-center justify-center">
                    <div className="animate-pulse h-5 w-16 bg-gray-200 rounded" />
                </div>
            </div>
        );
    }

    if ( error != null ) {
        return compact
            ? null
            : (
            <div className={`toll-price-card rounded shadow p-3 ${ className }`}>
                <div className="flex items-center text-red-500">
                    <small>
{error}
                    </small>
                </div>
            </div>
            );
    }

    const formattedPrice = tollPrice !== null ? `${ tollPrice.toFixed( 2 ) } CHF` : "N/A";

    if ( compact ) {
        return (
            <div className={`inline-flex items-center ${ className }`}>
                <span className="text-primary">
{formattedPrice}
                </span>
            </div>
        );
    }

    return (
        <div className={`toll-price-card rounded shadow p-3 ${ className }`}>
            <div className="flex items-center justify-between">
                <span className="font-bold text-primary">
                    {formattedPrice}
                </span>
            </div>
        </div>
    );
};

export default TollPriceDisplay;
